// Governed SuperAdmin owner-plane API client.
//
// Plane isolation (constitutional): SuperAdmin owner routes authenticate with the LAYERED SUPERADMIN
// SESSION bearer (localStorage.superadmin_session), issued by the OTP+MFA login ceremony. This is a
// DIFFERENT authority from the Business/Firebase plane — the shared lib/apiClient.js attaches
// auth.currentUser.getIdToken() (Firebase), which is null in the SuperAdmin portal (no Firebase login),
// so owner routes behind require_superadmin_session reject it with 401.
//
// This client is the single governed place that reads the SuperAdmin session token for fetch()-based
// owner calls, so feature modules never re-implement token plumbing and a Business bearer can never be
// attached to an owner request (or vice-versa). Errors surface truthfully (status preserved) so callers
// can show a real unauthorized state instead of a false empty result.
const BASE = process.env.REACT_APP_API_BASE || process.env.REACT_APP_BACKEND_URL || '';

// Canonical SuperAdmin session key is `superadmin_session` (set by SuperAdminAuthContext.finalizeSession).
// Legacy aliases are read too, matching the existing UserManagement/DashboardHome resolution order.
export function superAdminToken() {
  try {
    return localStorage.getItem('superadmin_session')
      || localStorage.getItem('superadmin_session_token')
      || localStorage.getItem('superAdminToken')
      || localStorage.getItem('superadminToken')
      || null;
  } catch (e) {
    return null;
  }
}

export function superAdminHasSession() {
  return !!superAdminToken();
}

async function saRequest(method, path, body) {
  const headers = { 'Content-Type': 'application/json' };
  const token = superAdminToken();
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const opts = { method, headers, credentials: 'include' };
  if (body !== undefined) opts.body = JSON.stringify(body || {});
  const res = await fetch(`${BASE}${path}`, opts);
  if (!res.ok) {
    const err = new Error(res.status === 401
      ? 'Your SuperAdmin session is not authorized. Please sign in again.'
      : `${res.status} ${res.statusText}`);
    err.status = res.status;
    throw err;
  }
  return res.json();
}

export const saGet = (path) => saRequest('GET', path);
export const saPost = (path, body) => saRequest('POST', path, body);
export const saPut = (path, body) => saRequest('PUT', path, body);
export const saDelete = (path) => saRequest('DELETE', path);
